import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Grid,
  Container,
  Tabs,
  Tab,
  CircularProgress,
  Paper,
  Chip,
  Alert,
  LinearProgress
} from '@mui/material';
import {
  EmojiEvents,
  EmojiEvents as TrophyIcon,
  Psychology as PsychologyIcon,
  Stars as StarsIcon,
  CalendarMonth as CalendarIcon,
  Lock as LockIcon
} from '@mui/icons-material';
import MainLayout from '../../components/layout/MainLayout';
import { getAllAchievements, getUserAchievements, getAchievementStats } from '../../services/achievementService';
import { Achievement, StudentAchievement, AchievementStats } from '../../types/achievement';

// Import shared components
import AchievementCard from '../../components/achievements/AchievementCard';
import { getIconComponent, backgroundPattern } from '../../components/achievements/AchievementUtils';

// Achievement Stats Component
function AchievementStatsCard({ stats }: { stats: AchievementStats }) {
  return (
    <Paper 
      elevation={4} 
      sx={{ 
        p: 3, 
        mb: 4, 
        borderRadius: 3,
        background: 'linear-gradient(135deg, #ffffff 0%, #f7f9fc 100%)',
        border: '1px solid #e0e7ff',
        position: 'relative',
        overflow: 'hidden'
      }}
    >
      <Box sx={{ 
        position: 'absolute', 
        top: 0, 
        right: 0, 
        width: '150px', 
        height: '150px',
        background: 'radial-gradient(circle, rgba(63,81,181,0.08) 0%, rgba(63,81,181,0) 70%)',
        borderRadius: '0 0 0 100%',
        zIndex: 0
      }} />
      
      <Box sx={{ position: 'relative', zIndex: 1 }}>
        <Typography 
          variant="h5" 
          gutterBottom
          sx={{ 
            fontWeight: 700, 
            color: '#2C3E50',
            display: 'flex',
            alignItems: 'center',
            gap: 1
          }}
        >
          <StarsIcon sx={{ color: '#FFC107', fontSize: 32 }} />
          Your Achievement Progress
        </Typography>
        
        <Grid container spacing={3} sx={{ mb: 3, mt: 1 }}>
          <Grid item xs={12} sm={4}>
            <Box 
              sx={{ 
                textAlign: 'center',
                p: 2,
                background: 'linear-gradient(135deg, rgba(255,215,0,0.05) 0%, rgba(255,215,0,0.15) 100%)',
                borderRadius: 2,
                border: '1px solid rgba(255,215,0,0.2)',
                transition: 'transform 0.3s ease',
                '&:hover': {
                  transform: 'translateY(-5px)'
                }
              }}
            >
              <TrophyIcon sx={{ fontSize: 40, mb: 1, color: '#FFD700' }} />
              <Typography 
                variant="h3" 
                fontWeight="bold"
                sx={{ 
                  color: '#B8860B',
                  textShadow: '0px 2px 3px rgba(0,0,0,0.1)'
                }}
              >
                {stats.totalEarned}
              </Typography>
              <Typography 
                variant="body1" 
                sx={{ 
                  color: '#6D4C41',
                  fontWeight: 500
                }}
              >
                Achievements Earned
              </Typography>
            </Box>
          </Grid>
          <Grid item xs={12} sm={8}>
            <Box 
              sx={{ 
                mt: 2,
                p: 2,
                background: 'rgba(255,255,255,0.7)',
                borderRadius: 2,
                border: '1px solid rgba(0,0,0,0.05)',
                boxShadow: 'inset 0 1px 3px rgba(0,0,0,0.05)'
              }}
            >
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1, alignItems: 'center' }}>
                <Typography variant="body1" fontWeight={500} color="#37474F">
                  Achievement Progress
                </Typography>
                <Typography 
                  variant="h6" 
                  fontWeight="bold"
                  sx={{
                    color: stats.percentageEarned > 75 ? '#2E7D32' :
                           stats.percentageEarned > 50 ? '#1976D2' :
                           stats.percentageEarned > 25 ? '#ED6C02' : '#D32F2F',
                    backgroundColor: stats.percentageEarned > 75 ? 'rgba(46,125,50,0.1)' :
                                     stats.percentageEarned > 50 ? 'rgba(25,118,210,0.1)' :
                                     stats.percentageEarned > 25 ? 'rgba(237,108,2,0.1)' : 'rgba(211,47,47,0.1)',
                    px: 1.5,
                    py: 0.5,
                    borderRadius: 10,
                    fontSize: '0.9rem'
                  }}
                >
                  {stats.percentageEarned}%
                </Typography>
              </Box>
              <Box sx={{ position: 'relative', height: 20, mb: 1 }}>
                <LinearProgress 
                  variant="determinate" 
                  value={stats.percentageEarned} 
                  sx={{ 
                    height: 14, 
                    borderRadius: 10,
                    backgroundColor: 'rgba(0,0,0,0.05)',
                    '& .MuiLinearProgress-bar': {
                      background: 'linear-gradient(90deg, #4CAF50 0%, #8BC34A 100%)',
                      borderRadius: 10,
                    }
                  }} 
                />
                <Box sx={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  width: '100%',
                  height: '100%',
                  border: '1px solid rgba(0,0,0,0.1)',
                  borderRadius: 10,
                  pointerEvents: 'none'
                }} />
              </Box>
              <Typography 
                variant="body2" 
                sx={{ 
                  mt: 1.5,
                  color: '#5C6BC0',
                  fontWeight: 500,
                  fontSize: '0.85rem',
                  fontStyle: 'italic',
                  position: 'relative',
                  pl: 3
                }}
              >
                <Box 
                  component="span" 
                  sx={{ 
                    position: 'absolute',
                    left: 0,
                    top: '50%',
                    transform: 'translateY(-50%)',
                    color: '#9C27B0',
                    fontSize: '1.2rem'
                  }}
                >
                  💡
                </Box>
                Keep solving problems to unlock more achievements!
              </Typography>
            </Box>
          </Grid>
        </Grid>
        
        {stats.highestRarity && stats.highestRarity.achievement && (
          <Box>
            <Typography 
              variant="subtitle1" 
              gutterBottom
              sx={{
                fontWeight: 600,
                color: '#37474F',
                display: 'flex',
                alignItems: 'center',
                gap: 1
              }}
            >
              <PsychologyIcon sx={{ color: '#9C27B0', fontSize: 24 }} />
              Your Rarest Achievement:
            </Typography>
            <Box 
              sx={{ 
                display: 'flex', 
                alignItems: 'center', 
                p: 2.5, 
                background: 'linear-gradient(135deg, rgba(156,39,176,0.03) 0%, rgba(156,39,176,0.12) 100%)',
                borderRadius: 2,
                border: '1px solid rgba(156,39,176,0.15)',
                transition: 'all 0.3s ease',
                '&:hover': {
                  boxShadow: '0 5px 15px rgba(0,0,0,0.08)',
                  transform: 'translateY(-3px)'
                }
              }}
            >
              <Box 
                sx={{ 
                  p: 2,
                  backgroundColor: 'white',
                  borderRadius: '50%',
                  boxShadow: '0 4px 8px rgba(0,0,0,0.1)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
              >
                {getIconComponent(stats.highestRarity.achievement.iconName)}
              </Box>
              <Box sx={{ ml: 3 }}>
                <Typography 
                  variant="h6"
                  sx={{
                    color: '#6A1B9A',
                    fontWeight: 600
                  }}
                >
                  {stats.highestRarity.achievement.name}
                </Typography>
                <Typography variant="body2" color="#616161" sx={{ my: 0.5 }}>
                  {stats.highestRarity.achievement.description}
                </Typography>
                <Typography 
                  variant="caption" 
                  sx={{
                    color: '#9575CD',
                    fontWeight: 500,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 0.5
                  }}
                >
                  <CalendarIcon sx={{ fontSize: 14 }} />
                  Earned on {new Date(stats.highestRarity.awardedAt).toLocaleDateString()}
                </Typography>
              </Box>
            </Box>
          </Box>
        )}
      </Box>
    </Paper>
  );
}

// Main Component
function AchievementsPage() {
  const [tabValue, setTabValue] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  
  const [allAchievements, setAllAchievements] = useState<Achievement[]>([]);
  const [userAchievements, setUserAchievements] = useState<StudentAchievement[]>([]);
  const [stats, setStats] = useState<AchievementStats>({
    totalEarned: 0,
    percentageEarned: 0,
    recentlyEarned: [],
    highestRarity: null
  });

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        setError(null);

        // Fetch achievements in sequence with better error handling
        try {
          const all = await getAllAchievements();
          setAllAchievements(all);
        } catch (error) {
          console.error("Error fetching all achievements:", error);
          setError("Failed to load achievement list. Please try again later.");
        }

        try {
          const user = await getUserAchievements();
          setUserAchievements(user);
        } catch (error) {
          console.error("Error fetching user achievements:", error);
          setError("Failed to load your achievements. Please try again later.");
        }

        try {
          const stats = await getAchievementStats();
          setStats(stats);
        } catch (error) {
          console.error("Error calculating achievement stats:", error);
          // Don't set error here, as we already have the basic achievement data
        }
      } catch (err) {
        console.error("Unexpected error fetching achievements:", err);
        setError("Failed to load achievements. Please try again later.");
      } finally {
        setLoading(false);
      }
    };
    
    fetchData();
  }, []);

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };
  
  // Get earned achievements IDs for quick lookup
  const earnedAchievementIds = new Set(userAchievements.map(ua => ua.achievementId));
  
  // Get the earned achievement date by achievement ID
  const getEarnedDate = (achievementId: number): Date | undefined => {
    const earned = userAchievements.find(ua => ua.achievementId === achievementId);
    return earned?.awardedAt;
  };

  return (
    <MainLayout>
      <Container>
        <Box 
          sx={{ 
            display: 'flex', 
            flexDirection: { xs: 'column', sm: 'row' }, 
            alignItems: 'center',
            justifyContent: 'space-between',
            mb: 2,
            position: 'relative',
            overflow: 'hidden',
            borderRadius: 2,
            background: 'linear-gradient(135deg, #3f51b5 0%, #303f9f 100%)',
            px: 3,
            py: 2,
            color: 'white',
            boxShadow: '0 4px 20px rgba(0,0,0,0.15)'
          }}
        >
          <Box 
            sx={{ 
              position: 'absolute',
              top: 0,
              left: 0,
              width: '100%',
              height: '100%',
              opacity: 0.1,
              background: backgroundPattern,
              zIndex: 0
            }}
          />
          <Box sx={{ position: 'relative', zIndex: 1 }}>
            <Typography 
              variant="h4" 
              component="h1" 
              sx={{ 
                fontWeight: 700,
                background: 'linear-gradient(90deg, #FFFFFF 0%, #E8EAF6 100%)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                display: 'inline-flex',
                alignItems: 'center',
                gap: 1.5,
                textShadow: '0 2px 4px rgba(0,0,0,0.2)'
              }}
            >
              <EmojiEvents sx={{ color: '#FFD700', fontSize: 36 }} />
              Achievement Trophy Case
            </Typography>
            <Typography 
              variant="body1" 
              sx={{ 
                color: 'rgba(255,255,255,0.9)',
                fontWeight: 500,
                mt: 1,
                textShadow: '0 1px 2px rgba(0,0,0,0.1)'
              }}
            >
              Track your progress and showcase your math achievements!
            </Typography>
          </Box>
          <Box 
            sx={{ 
              display: { xs: 'none', md: 'block' },
              position: 'relative',
              zIndex: 1
            }}
          >
            <Box
              sx={{
                position: 'relative',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              {/* Trophy shine effect */}
              <Box
                sx={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  width: '100%',
                  height: '100%',
                  background: 'radial-gradient(circle, rgba(255,255,255,0.3) 0%, rgba(255,255,255,0) 70%)',
                  borderRadius: '50%',
                  animation: 'pulse 2s infinite',
                  '@keyframes pulse': {
                    '0%': { opacity: 0.7, transform: 'scale(1)' },
                    '50%': { opacity: 0.3, transform: 'scale(1.2)' },
                    '100%': { opacity: 0.7, transform: 'scale(1)' },
                  },
                }}
              />
              {/* Multiple trophy icons for decorative effect */}
              <TrophyIcon sx={{ color: '#FFD700', fontSize: 80 }} />
            </Box>
          </Box>
        </Box>
        
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
            <CircularProgress />
          </Box>
        ) : error ? (
          <Alert severity="error" sx={{ my: 2 }}>
            {error}
          </Alert>
        ) : allAchievements.length === 0 ? (
          <Alert severity="warning" sx={{ my: 2 }}>
            No achievements found in the system. The achievement system may not be properly set up.
          </Alert>
        ) : (
          <>
            <AchievementStatsCard stats={stats} />
            
            <Box 
              sx={{ 
                mb: 4,
                mt: 2,
                borderRadius: 2,
                overflow: 'hidden',
                boxShadow: '0 2px 8px rgba(0,0,0,0.08)'
              }}
            >
              <Tabs 
                value={tabValue} 
                onChange={handleTabChange}
                indicatorColor="primary"
                textColor="primary"
                centered
                sx={{
                  background: 'linear-gradient(180deg, #f5f5f5 0%, #e8eaf6 100%)',
                  borderRadius: 2,
                  '& .MuiTabs-indicator': {
                    height: 3,
                    borderRadius: '3px 3px 0 0'
                  },
                  '& .MuiTab-root': {
                    fontWeight: 600,
                    fontSize: '0.95rem',
                    transition: 'all 0.2s ease',
                    py: 2,
                    '&:hover': {
                      color: '#3f51b5',
                      backgroundColor: 'rgba(63, 81, 181, 0.08)'
                    },
                    '&.Mui-selected': {
                      color: '#3f51b5',
                      fontWeight: 700
                    }
                  }
                }}
              >
                <Tab 
                  label={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <StarsIcon fontSize="small" />
                      <span>All Achievements</span>
                    </Box>
                  } 
                />
                <Tab 
                  label={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <TrophyIcon fontSize="small" sx={{ color: '#FFD700' }} />
                      <span>Earned</span>
                      <Chip 
                        label={stats.totalEarned} 
                        size="small"
                        sx={{ 
                          ml: 0.5,
                          fontWeight: 'bold',
                          backgroundColor: '#4CAF50',
                          color: 'white'
                        }} 
                      />
                    </Box>
                  } 
                />
                <Tab 
                  label={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <LockIcon fontSize="small" />
                      <span>Locked</span>
                    </Box>
                  } 
                />
              </Tabs>
            </Box>
            
            <Grid container spacing={3} data-demo-id="achievements-grid">
              {allAchievements.length === 0 ? (
                <Box sx={{ textAlign: 'center', width: '100%', py: 3 }}>
                  <Typography variant="body1">No achievements found</Typography>
                </Box>
              ) : (
                <>
                  {tabValue === 0 && allAchievements
                    .sort((a, b) => {
                      // Sort earned achievements first, then by ID
                      const aEarned = earnedAchievementIds.has(a.id);
                      const bEarned = earnedAchievementIds.has(b.id);
                      if (aEarned && !bEarned) return -1;
                      if (!aEarned && bEarned) return 1;
                      return a.id - b.id;
                    })
                    .map(achievement => (
                      <Grid item xs={12} sm={6} md={4} key={achievement.id}>
                        <AchievementCard 
                          achievement={achievement} 
                          earned={earnedAchievementIds.has(achievement.id)}
                          earnedDate={getEarnedDate(achievement.id)}
                        />
                      </Grid>
                    ))}
                  
                  {tabValue === 1 && (
                    userAchievements.length > 0 ? (
                      userAchievements
                        .filter(ua => ua.achievement) // Ensure achievement data exists
                        .sort((a, b) => {
                          // Sort by unlocked status first, then by award date (newest first)
                          if (a.achievement && !b.achievement) return -1;
                          if (!a.achievement && b.achievement) return 1;
                          return new Date(b.awardedAt).getTime() - new Date(a.awardedAt).getTime();
                        })
                        .map(userAchievement => userAchievement.achievement ? (
                          <Grid item xs={12} sm={6} md={4} key={userAchievement.id}>
                            <AchievementCard
                              achievement={userAchievement.achievement}
                              earned={true}
                              earnedDate={userAchievement.awardedAt}
                            />
                          </Grid>
                        ) : null)
                    ) : (
                      <Box sx={{ textAlign: 'center', width: '100%', py: 3 }}>
                        <Typography variant="body1">
                          You haven't earned any achievements yet. Keep working on problems to unlock achievements!
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                          Check out all available achievements in the "All Achievements" tab to see what you can earn.
                        </Typography>
                        <Box sx={{ maxWidth: 300, mx: 'auto', mt: 3 }}>
                          <Alert severity="info">
                            Tip: Try solving your first math problem to earn the "Math Rookie" achievement!
                          </Alert>
                        </Box>
                      </Box>
                    )
                  )}
                  
                  {tabValue === 2 && (
                    allAchievements
                      .filter(achievement => !earnedAchievementIds.has(achievement.id))
                      .length > 0 ? (
                        allAchievements
                          .filter(achievement => !earnedAchievementIds.has(achievement.id))
                          .map(achievement => (
                            <Grid item xs={12} sm={6} md={4} key={achievement.id}>
                              <AchievementCard 
                                achievement={achievement} 
                                earned={false}
                              />
                            </Grid>
                          ))
                      ) : (
                        <Box sx={{ textAlign: 'center', width: '100%', py: 3 }}>
                          <Typography variant="h6" color="primary">
                            Congratulations! You've earned all available achievements!
                          </Typography>
                          <Typography variant="body1" sx={{ mt: 2 }}>
                            You've mastered all the challenges. Keep up the great work!
                          </Typography>
                        </Box>
                      )
                  )}
                </>
              )}
            </Grid>
          </>
        )}
      </Container>
    </MainLayout>
  );
}

export default AchievementsPage;